// Shared notification lookup tables, types, and constants.

import { NotificationTypeEnum, type Notification } from 'src/api-sdk';

export type NotificationType =
  | Notification['notification_type']
  | 'BATCH_REGISTRATION_UPDATE'
  | 'BATCH_COMPLIANCE_CHECK_UPDATE'
  | 'USER_DETAILS_UPDATE';

// Temporary/frontend-only types can live here, but backend enum values are read
// directly from the generated SDK. After the backend adds a notification type
// and the SDK is regenerated, it appears in the Administration tab by default.
const ADDITIONAL_NOTIFICATION_TYPES = ['USER_DETAILS_UPDATE'] as const;

// Fields the backend may include in a notification payload.
export type NotificationTemplateData = {
  [key: string]: unknown;
  user_name?: string;
  comment_text?: string;
  binder_name?: string;
  company_name?: string;
  compliance_check_name?: string;
  article_name?: string;
  feedback_message?: string | number;
  support_request_subject?: string;
  support_user_name?: string;
  investigation_status?: string | number;
  batch_name?: string | number;
  status?: string | number;
  status_display?: string;
  completed?: string | number;
  total?: string | number;
  export_type?: string | number;
  file_type?: string | number;
  updated_field?: string;
  new_value?: string;
  variant?: string;
  exportName?: string;
  export_name?: string;
  fileName?: string;
  file_name?: string;
  filename?: string;
  name?: string;
  download_url?: string | null;
  icon_url?: string;
};

// Label for unknown backend notification types.
export const DEFAULT_NOTIFICATION_LABEL = 'Notifications';

const NOTIFICATION_CATEGORIES = [
  'Mentions',
  'Articles',
  'Companies',
  'Binders',
  'Administration',
] as const;

export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

// Inbox filter options and matching tab labels.
export const notificationTypeOptions: NotificationCategory[] = [
  ...NOTIFICATION_CATEGORIES,
];

type NotificationTypeConfig = {
  category: NotificationCategory;
  showProgress?: boolean;
};

// Single registry for known notification behavior. Add new backend types here
// first; tabs, labels, counts, triggers, and progress handling are derived.
export const notificationTypeConfig: Partial<
  Record<NotificationType, NotificationTypeConfig>
> = {
  BINDER_COMMENT_MENTION: {
    category: 'Mentions',
  },
  COMMENT_MENTION: {
    category: 'Mentions',
  },
  BINDER_COMMENT: {
    category: 'Binders',
  },
  COMPANY_REGISTRATION_UPDATE: {
    category: 'Companies',
    showProgress: true,
  },
  COMPANY_FEEDBACK_RECEIVED: {
    category: 'Companies',
  },
  MISSING_COMPANY_INVESTIGATION_UPDATE: {
    category: 'Companies',
  },
  COMPLIANCE_CHECK_UPDATE: {
    category: 'Companies',
    showProgress: true,
  },
  COMMENT: {
    category: 'Companies',
  },
  NEWS_ARTICLE_FEEDBACK_RECEIVED: {
    category: 'Articles',
  },
  USER_DETAILS_UPDATE: {
    category: 'Administration',
  },
  EXPORT_UPDATE: {
    category: 'Administration',
    showProgress: true,
  },
  SUPPORT_REQUEST_UPDATE: {
    category: 'Administration',
  },
  BATCH_REGISTRATION_UPDATE: {
    category: 'Companies',
    showProgress: true,
  },
  BATCH_COMPLIANCE_CHECK_UPDATE: {
    category: 'Companies',
    showProgress: true,
  },
};

export const knownNotificationTypes = Array.from(
  new Set([
    ...(Object.values(NotificationTypeEnum) as NotificationType[]),
    ...ADDITIONAL_NOTIFICATION_TYPES,
  ])
);

export const isNotificationCenterNotificationType = (
  notificationType: string | null | undefined
): notificationType is NotificationType =>
  knownNotificationTypes.includes(notificationType as NotificationType);

export const getNotificationTypeConfig = (notificationType: NotificationType) =>
  notificationTypeConfig[notificationType];

export const getNotificationTypeCategory = (
  notificationType: NotificationType
): NotificationCategory =>
  getNotificationTypeConfig(notificationType)?.category ?? 'Administration';

export const getNotificationTypeLabel = (
  notificationType: NotificationType,
  defaultTypeLabel: string = DEFAULT_NOTIFICATION_LABEL
) => getNotificationTypeConfig(notificationType)?.category ?? defaultTypeLabel;

// Notification types that show a progress bar.
export const PROGRESS_NOTIFICATION_TYPES: NotificationType[] =
  knownNotificationTypes.filter(
    (notificationType) =>
      getNotificationTypeConfig(notificationType)?.showProgress
  );

// Backend notification types that belong to each inbox tab.
// Tabs pass these arrays to the backend notification_type filter.
export const notificationTypesByTabLabel: Partial<
  Record<NotificationCategory, NotificationType[]>
> = notificationTypeOptions.reduce(
  (acc, category) => {
    acc[category] = knownNotificationTypes.filter(
      (notificationType) =>
        getNotificationTypeCategory(notificationType) === category
    );
    return acc;
  },
  {} as Record<NotificationCategory, NotificationType[]>
);
